import React, { useEffect } from 'react';
import { ProgressBar, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import Snackbar from 'components/DesignSystem/Snackbar';
import formatBytes from 'helper/formatBytes';
import { getFileDownloadControls } from 'helper/fileDownloadControls';
import { FileDownload, downloadDismissed, selectFileDownload } from 'redux/reducers/fileDownloadsReducer';

import './index.scss';

export const STARTED_AUTO_CLOSE_MS = 10_000;

function startedText({ tamanhoBytes }: FileDownload) {
  const size = tamanhoBytes ? `: ${formatBytes(tamanhoBytes)}` : '';
  return `Download iniciado${size}. Acompanhe na barra de downloads do navegador.`;
}

function progressText({ progresso, tamanhoBytes }: FileDownload) {
  if (progresso === null) return 'Baixando…';
  const size = tamanhoBytes ? ` de ${formatBytes(tamanhoBytes)}` : '';
  return `Baixando… ${progresso}%${size}`;
}

// Card flutuante do download em andamento. Montado uma vez no layout, alimentado pelo slice
// `fileDownloads`: continua visível quando o usuário troca de aba/página no meio do download.
export default function DownloadProgressCard() {
  const dispatch = useDispatch();
  const download = useSelector(selectFileDownload);
  const id = download?.id;
  const fase = download?.fase;

  useEffect(() => {
    if (!id || fase !== 'iniciado') return undefined;
    const timer = setTimeout(() => dispatch(downloadDismissed(id)), STARTED_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [dispatch, id, fase]);

  if (!download) return null;

  const { retry, cancel } = getFileDownloadControls(download.id);
  const close = () => dispatch(downloadDismissed(download.id));
  const title = <div className="download-progress-card-name">{download.nome}</div>;

  if (download.fase === 'preparando') {
    return (
      <div className="download-progress-card" role="status">
        <Snackbar variant="primary">
          {title}
          <div className="d-flex align-items-center gap-2">
            <Spinner size="sm" />
            <span>{download.mensagem ?? 'Preparando o arquivo no servidor…'}</span>
          </div>
        </Snackbar>
      </div>
    );
  }

  if (download.fase === 'baixando') {
    return (
      <div className="download-progress-card" role="status">
        <Snackbar
          variant="primary"
          actionText={cancel ? 'Cancelar' : ''}
          onAction={cancel}
          actionOnASecondLine
        >
          {title}
          <div>{progressText(download)}</div>
          <ProgressBar
            className="mt-2"
            now={download.progresso ?? 100}
            animated={download.progresso === null}
            striped={download.progresso === null}
            aria-label="Progresso do download"
          />
        </Snackbar>
      </div>
    );
  }

  if (download.fase === 'erro') {
    return (
      <div className="download-progress-card" role="alert">
        <Snackbar
          variant="danger"
          closeButton
          onClose={close}
          actionText={retry ? 'Tentar de novo' : ''}
          onAction={() => { close(); retry?.(); }}
          actionOnASecondLine
        >
          {title}
          <div>{download.mensagem}</div>
        </Snackbar>
      </div>
    );
  }

  return (
    <div className="download-progress-card" role="status">
      <Snackbar variant="success" closeButton onClose={close}>
        {title}
        <div>{download.mensagem ?? startedText(download)}</div>
      </Snackbar>
    </div>
  );
}
